import * as React from 'react';
import { FieldProps } from '../metadataFormField';
import { cn, formatDate, parseDate } from '../../../utils';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import './complimentaryResearchField.scss';
import { useSelector } from '../../../utils/useSelector';
import { useLoader } from '../../Loader/loader';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { Icon, Trash } from 'rcd-cpp-ui/ui/icon';
import { Metadata } from '../../../types/Metadata';
import { Group } from 'rcd-cpp-ui/ui/form';
import { DatePicker } from '../../DatePicker/datePicker';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';
import { API } from '../../../api';
import { Add } from '../../Icon/icon';

export const ComplimentaryResearchField = (props: FieldProps) => {
  const { field, data, disabled, updater } = props;

  const [showModal, setShowModal] = React.useState(false);
  const complimentaryResearches = getComplimentaryResearches(data);

  const { openModal, closeModal } = React.useMemo(
    () => ({
      openModal: () => setShowModal(true),
      closeModal: () => setShowModal(false)
    }),
    [setShowModal]
  );

  return (
    <>
      <FieldGroup
        {...props}
        disabled={false}
        value={null}
        componentRight={
          !disabled ? (
            <div className="fgl-link" onClick={openModal}>
              {!complimentaryResearches?.length ? 'Add' : 'Edit'}
            </div>
          ) : null
        }
      >
        <div className="complimentary-research-field">
          {!complimentaryResearches.length && '--'}
          {complimentaryResearches.map(cr => (
            <div className="card" key={cr.key}>
              <div>
                <Group label="Start Date">{cr.startDateStr}</Group>
                <Group label="End Date">{cr.endDateStr}</Group>
              </div>
              <Group label="Entitlement">
                <div className="crf-entitlements">
                  {cr.entitlements.map(e => (
                    <span key={e.props.refId}>{e.props.abbr}</span>
                  ))}
                </div>
              </Group>
            </div>
          ))}
        </div>
      </FieldGroup>
      {showModal && (
        <ComplimentaryResearchModal
          onClose={closeModal}
          data={data}
          onSave={async entitlements => {
            updater.updateField(field, entitlements);
            await updater.update();
            closeModal();
          }}
        />
      )}
    </>
  );
};

type Props = {
  onClose: () => void;
  onSave: (entitlements: Metadata[]) => Promise<any>;
  data: Partial<Metadata>;
};

const mockAPI = (entitlements: Metadata[]) =>
  React.useMemo<Pick<API, 'searchReferences'>>(() => {
    return {
      searchReferences: async payload => {
        if (!entitlements) return [];
        const q = payload?.q?.toLowerCase();
        return entitlements.filter(e => {
          if (!e?.props?.isComplimentaryResearch) return false;
          if (!!q) return e.props.abbr?.toLowerCase().includes(q);
          return true;
        });
      }
    };
  }, [entitlements]);

const ComplimentaryResearchModal = ({ onClose, data, ...p }: Props) => {
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const [complimentaryResearches, setComplimentaryResearches] = React.useState(
    getComplimentaryResearches(data)
  );
  const [entitlements, setEntitlements] = React.useState<Metadata[]>(null);
  const { Loader, setLoading, onSave } = useLoader({ onSave: p.onSave });
  React.useEffect(() => {
    if (!entitlements && !!getAPI) {
      const { searchReferences } = getAPI();
      setLoading(true);
      searchReferences({
        nodeType: NodeType.Entitlement,
        maxResults: 100000
      }).then(data => {
        setEntitlements(data);
        setLoading(false);
      });
    }
  }, [getAPI]);
  React.useEffect(() => {
    if (complimentaryResearches.length == 0) {
      addMore();
    }
  }, []);

  const api = mockAPI(entitlements);
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const {
    addMore,
    removeCR,
    updateEntitlements,
    setStartDate,
    setEndDate,
    valuesToSave
  } = React.useMemo(() => {
    const addMore = () =>
      setComplimentaryResearches(crs => [
        ...crs,
        {
          isNew: true,
          key: 'new_' + Date.now(),
          startDate: null,
          startDateStr: null,
          endDate: null,
          endDateStr: null,
          entitlements: []
        }
      ]);
    const removeCR = (cr: ComplementaryResearch) =>
      setComplimentaryResearches(crs => crs.filter(c => c.key != cr.key));
    const setDate =
      (p: string, pStr: string) => (cr: ComplementaryResearch, d: Date) =>
        setComplimentaryResearches(crs =>
          crs.map(c => {
            if (c.key == cr.key) {
              const val = formatDate(d, true);
              c.entitlements.forEach(e => (e.relationship.props[p] = val));
              c[p] = d;
              c[pStr] = formatDate(d);
              return { ...c };
            }
            return c;
          })
        );
    const updateEntitlements = (
      cr: ComplementaryResearch,
      entitlements: Metadata[]
    ) =>
      setComplimentaryResearches(crs =>
        crs.map(c => {
          if (c.key == cr.key) {
            const startDate = formatDate(c.startDate, true);
            const endDate = formatDate(c.endDate, true);
            return {
              ...c,
              entitlements: entitlements.map(e => ({
                ...e,
                relationship: {
                  type: null,
                  props: { startDate, endDate, field: null }
                }
              }))
            };
          }
          return c;
        })
      );

    let valuesToSave = complimentaryResearches.reduce((vals, cr) => {
      if (!vals || !cr.startDate || !cr.endDate || !cr.entitlements?.length)
        return null;
      return vals.concat(cr.entitlements);
    }, [] as Metadata[]);

    if (valuesToSave && valuesToSave.length == 0) {
      if (!getComplimentaryResearches(data)?.length) valuesToSave = null;
    }

    return {
      addMore,
      removeCR,
      updateEntitlements,
      setStartDate: setDate('startDate', 'startDateStr'),
      setEndDate: setDate('endDate', 'endDateStr'),
      valuesToSave
    };
  }, [complimentaryResearches]);

  return (
    <div className="slider-activator">
      <Modal
        id="complimentaryResearchModal"
        onClose={onClose}
        header="Complimentary Research"
        footer={
          <Buttons>
            <Secondary label="Cancel" onClick={onClose} />
            <Primary
              label="Save"
              disabled={!valuesToSave}
              onClick={() => onSave(valuesToSave)}
            />
          </Buttons>
        }
      >
        <Loader />
        <div className="scrollable">
          {complimentaryResearches.map(cr => {
            const isStartDateStarted =
              !cr.isNew && isBefore(cr.startDate, tomorrow);
            const isEndDateStarted =
              !cr.isNew && isBefore(cr.endDate, tomorrow);
            const hasPublished = isStartDateStarted || isEndDateStarted;
            return (
              <div
                className={cn('card', {
                  'fixed-card': hasPublished
                })}
                key={cr.key}
              >
                <div>
                  <Group label="Start Date">
                    <DatePicker
                      value={cr.startDateStr}
                      onChange={d => setStartDate(cr, d)}
                      minDate={tomorrow}
                      maxDate={cr.endDate}
                      disabled={isStartDateStarted}
                    ></DatePicker>
                  </Group>
                  <Group label="End Date">
                    <DatePicker
                      value={cr.endDateStr}
                      onChange={d => setEndDate(cr, d)}
                      minDate={maxDate(cr.startDate, tomorrow)}
                      disabled={isEndDateStarted}
                    ></DatePicker>
                  </Group>
                </div>
                <Group label="Entitlement">
                  <ReferenceSearch
                    onChange={values => updateEntitlements(cr, values)}
                    api={api}
                    nodeType={NodeType.Entitlement}
                    multiple={true}
                    maxResults={50}
                    minLength={0}
                    selected={cr.entitlements}
                    disabled={hasPublished}
                  />
                </Group>
                {!hasPublished && (
                  <Icon onClick={() => removeCR(cr)}>
                    <Trash />
                  </Icon>
                )}
              </div>
            );
          })}
          <div className="add-more">
            <span onClick={addMore}>
              <Add /> Add More
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const isBefore = (d1: Date, d2: Date) => {
  if (d1 == null || d2 == null) return false;
  return d2.getTime() > d1.getTime();
};

const maxDate = (d1: Date, d2: Date) => {
  if (d1 == null) return d2;
  if (d2 == null) return d1;
  if (d1.getTime() > d2.getTime()) return d1;
  return d2;
};

type ComplementaryResearch = {
  isNew?: boolean;
  key: string; // startDate_endDate
  startDate: Date;
  startDateStr: string;
  endDate: Date;
  endDateStr: string;
  entitlements: Metadata[];
};

const getComplimentaryResearches = (data: Partial<Metadata>) => {
  if (!data?.props) return [];
  const ms = data.props.complimentaryResearch as Metadata[];
  if (!ms) return [];
  if (!Array.isArray(ms)) return [];
  const crs = ms.reduce((crs, m) => {
    if (!m?.props) return crs;
    const startDate = parseDate(m.relationship.props.startDate);
    const endDate = parseDate(m.relationship.props.endDate);
    const key = startDate.getTime() + '_' + endDate.getTime();
    const cr = findOne(crs, { key });
    if (!cr) {
      crs.push({
        key,
        startDate,
        startDateStr: formatDate(startDate),
        endDate,
        endDateStr: formatDate(endDate),
        entitlements: [m]
      });
    } else {
      cr.entitlements.push(m);
    }
    return crs;
  }, [] as ComplementaryResearch[]);
  crs.forEach(cr =>
    cr.entitlements?.sort((a, b) => a.props.abbr.localeCompare(b.props.abbr))
  );
  return crs;
};
